import React, { useCallback, useEffect, useRef } from 'react';

/**
 * Component to detect when a component mounts or unmounts for real, as opposed to other events that
 * can look like mounting/unmounting but aren't. (For example, an effect with an empty dependency
 * list is called and cleaned up when an Activity becomes visible or hidden, even though the
 * component technically remains mounted throughout.)
 *
 * Use this sparingly, such as to clean up component side-effects that should persist while the
 * current tab is inactive. For anything else, useEffect should be sufficient.
 */
export function ComponentLifecycleCallbacks(props: {
  onMount?: () => void;
  onUnmount?: () => void;
}) {
  const { onMount, onUnmount } = props;

  // Latest-callback refs keep the marker ref callback stable across renders.
  const isAttached = useRef(false);
  const hasUnmounted = useRef(false);
  const onMountRef = useRef(onMount);
  const onUnmountRef = useRef(onUnmount);
  const domElement = useRef<HTMLElement | null>(null);
  const removalObserver = useRef<MutationObserver | null>(null);

  const fireUnmount = useCallback(() => {
    if (hasUnmounted.current) return;

    hasUnmounted.current = true;
    removalObserver.current?.disconnect();
    removalObserver.current = null;
    onUnmountRef.current?.();
  }, []);

  const ref = useCallback(
    (el: HTMLElement | null) => {
      if (el) {
        isAttached.current = true;
        // Shown again after an Activity hide — the pending removal watch is no longer needed.
        removalObserver.current?.disconnect();
        removalObserver.current = null;

        if (!domElement.current) {
          domElement.current = el;
          onMountRef.current?.();
        }

        return;
      }

      if (!domElement.current) return;
      isAttached.current = false;

      /**
       * Like effects, ref callbacks are called when an activity becomes hidden or visible. To check
       * if this is a real unmount, we also need to check if the DOM element still exists in the
       * DOM, which we do after a short delay to ensure React has time to remove it.
       */
      queueMicrotask(() => {
        if (isAttached.current || hasUnmounted.current) return;
        if (!document.body.contains(domElement.current)) {
          fireUnmount();
          return;
        }

        /**
         * The ref detached but the marker is still in the DOM: the Activity is hidden, not
         * unmounted. If the hidden subtree is later deleted, React never calls this ref again
         * (it was already detached on hide), so watch for the marker's removal directly. The
         * observer is disconnected when the Activity is shown again or after firing.
         */
        removalObserver.current?.disconnect();
        removalObserver.current = new MutationObserver(() => {
          if (!document.body.contains(domElement.current)) fireUnmount();
        });
        removalObserver.current.observe(document.body, { childList: true, subtree: true });
      });
    },
    [fireUnmount]
  );

  useEffect(() => {
    onMountRef.current = onMount;
    onUnmountRef.current = onUnmount;
  });

  return <div ref={ref} style={{ display: 'none' }} />;
}
